// Pure helpers for a conversational turn: what we send (open fields), and what
// we render back (receipts). Kept out of the provider so both are unit-testable
// without a network or a React tree.

import { Answer, FormFill, OpenFieldSpec } from '../../API/types';
import { FillReceipt } from '../Shared/API/ApiTypes';
import { CfRegistry } from './formsConfig';
import { CfField, CfForm, FieldValue } from './types';
import { displayValue, normalize } from './valueFormat';

/** Serialize open fields for the extraction contract: id, type, label, options,
 *  so the service can return canonical values (option values, not labels).
 *  `sensitive` and `noAI` ride along because the service drops guardrail fields
 *  before the message reaches the model. */
export const toOpenFieldSpecs = (fields: CfField[]): OpenFieldSpec[] =>
    fields.map((f) => ({
        id: f.id,
        type: f.type,
        label: f.label,
        options: f.options ?? [],
        optional: f.optional,
        sensitive: f.sensitive === true,
        noAI: f.noAI === true,
    }));

/**
 * How a turn resolved, for the three surfaces it can produce.
 *
 * - `ok` — reply, plus receipts for anything filled.
 * - `retryable` — the turn produced nothing usable and re-running the identical
 *   message is honest: a failed call, a timeout, or an extraction error the
 *   backend flagged retryable.
 * - `unrecorded` — the answer arrived but extraction failed in a way retrying
 *   cannot fix. The reply is shown normally with a notice, and no retry action,
 *   because an identical internal failure fails identically.
 */
export type TurnOutcome = 'ok' | 'retryable' | 'unrecorded';

export const classifyTurn = (result: Answer | null | undefined): TurnOutcome => {
    if (!result || !result.answer) return 'retryable';
    const error = result.extraction_error;
    if (!error) return 'ok';
    // Only an explicit `retryable: false` earns the non-retry surface. An error
    // whose shape we don't recognize — no flag, a null, a future field — is
    // treated as retryable, because the one outcome a failure must never take
    // is a silent success.
    return error.retryable === false ? 'unrecorded' : 'retryable';
};

export type NormalizedFill = {
    fieldId: string;
    value: FieldValue;
    /**
     * The turn form's own definition, or null when the turn filled a field of
     * another form — a different registry form, or one this widget holds no
     * definition for at all. Non-own fills are display-only: they render as
     * receipts so the fill is never invisible (labels resolve from the registry
     * when it knows the field), but they never reach `values`, progress,
     * next-required, or the open-field list.
     */
    field: CfField | null;
    /** Label to render: the local definition's, or the payload's own. */
    label: string;
    /** No value is available to store or show (withheld by contract, or absent). */
    withheld: boolean;
    /** Render the field's name without its value. */
    sensitive: boolean;
    /** The payload's value as sent, used to render a fill we cannot format
     *  locally. Empty for a field we hold a definition for. */
    rawValue: string;
};

/** The fills that carry a value this form can actually commit. Foreign fills
 *  are excluded: they belong to a form this widget does not own. */
export const committableFills = (fills: NormalizedFill[]): NormalizedFill[] =>
    fills.filter((f) => !f.withheld && f.field !== null);

/** Did this turn touch a field of *our* form? Drives implicit activation, which
 *  a foreign form's fill must not trigger. */
export const hasLocalFill = (fills: NormalizedFill[]): boolean =>
    fills.some((f) => f.field !== null);

/** A foreign value has no local type to format against, so it renders as the
 *  payload sent it. */
const rawDisplay = (value: FormFill['field_value']): string =>
    Array.isArray(value) ? value.join(', ') : String(value ?? '');

/** The registry field that owns `fieldId`, searched outside `exclude` — how a
 *  fill for a non-active registry form resolves its label and display format. */
const registryFieldFor = (
    registry: CfRegistry | null | undefined,
    fieldId: string,
    exclude: CfForm | null
): CfField | null => {
    if (!registry) return null;
    for (const other of registry.forms) {
        if (other === exclude) continue;
        const match = other.fields.find((f) => f.id === fieldId);
        if (match) return match;
    }
    return null;
};

/**
 * Match a turn's wire fills back to field definitions and normalize each value
 * into storage format. `form` is the form the turn ran for (the active form,
 * or the config form before anything starts) — only its fills are committable.
 *
 * A fill naming a field with no definition in `form` is kept rather than
 * dropped: the platform receipts requirement is that a fill is never invisible
 * to the visitor, and the bot legitimately fills forms beyond the active one.
 * When another *registry* form owns the field, its definition supplies the
 * label and display formatting (multiform slice); a truly foreign fill renders
 * from the payload's own `label` and `field_value`. Neither goes any further.
 */
export const normalizeFills = (
    form: CfForm | null,
    fills: FormFill[] | undefined,
    registry?: CfRegistry | null
): NormalizedFill[] => {
    if (!fills?.length) return [];
    return fills.map((fill) => {
        const field = form?.fields.find((f) => f.id === fill.field_id) ?? null;
        const registryField = field ? null : registryFieldFor(registry, fill.field_id, form);
        // Sensitivity can come from either side: the payload flags it on the
        // turn path, and a local definition may mark a field the payload did not.
        const sensitive =
            fill.sensitive === true || field?.sensitive === true || registryField?.sensitive === true;
        // A withheld fill arrives with `field_value` omitted by contract. There
        // is nothing to store, but the receipt still has to name the field.
        const withheld = fill.field_value == null;
        const value = withheld || !field
            ? ''
            : normalize(field, fill.field_value as FieldValue);
        return {
            fieldId: fill.field_id,
            value,
            field,
            // Falls back to the id only if no definition and no payload label —
            // a bare id reads poorly, but an unnamed fill would be worse.
            label: field?.label || registryField?.label || fill.label || fill.field_id,
            withheld,
            sensitive,
            rawValue: field
                ? ''
                : registryField && !withheld
                    ? displayValue(registryField, normalize(registryField, fill.field_value as FieldValue))
                    : rawDisplay(fill.field_value),
        };
    });
};

/**
 * Receipts for the turn's message. A sensitive field names itself and omits the
 * value — the platform forbids echoing it into the chat history. A foreign
 * field renders from the payload, styled identically: the visitor has no reason
 * to care which form a fill belongs to.
 */
export const toReceipts = (fills: NormalizedFill[]): FillReceipt[] =>
    fills.map(({ fieldId, field, value, label, withheld, sensitive, rawValue }) => ({
        fieldId,
        label,
        display: withheld || sensitive ? '' : field ? displayValue(field, value) : rawValue,
        sensitive: withheld || sensitive,
    }));

export type SaveField = {
    fieldid: string;
    fieldvalue: string;
    chatorigin: boolean;
    sensitive: boolean;
};

/**
 * Full field list for the save path — the submit contract overwrites wholesale,
 * so every known value is re-sent on each commit (platform: "clients MAY
 * re-submit the full field list on each confirm").
 *
 * `chatOriginIds` names only the fields *this* write is confirming, so the
 * backend emits one `FieldFilledViaChat` per confirmed suggestion at the write.
 * Fields confirmed by an earlier submit are re-sent unmarked — marking them
 * again would count the same fill twice. `sensitive` comes from the form
 * definition and rides every submit, so the event can name a sensitive field
 * without echoing its value.
 */
export const toSaveFields = (
    form: CfForm | null,
    values: Record<string, FieldValue>,
    chatOriginIds?: ReadonlySet<string>
): SaveField[] => {
    if (!form) return [];
    return form.fields
        .filter((f) => values[f.id] !== undefined)
        .map((f) => {
            const value = values[f.id];
            return {
                fieldid: f.id,
                fieldvalue: Array.isArray(value) ? value.join(',') : String(value ?? ''),
                chatorigin: chatOriginIds?.has(f.id) === true,
                sensitive: f.sensitive === true,
            };
        });
};

// There is deliberately no stream end-event reader here. The platform clause for
// receipts-on-`/questionstream` has no target: the deployed backend serves no
// streaming route (`conversational-forms-backend` recorded it as dormant, dead
// code), so a conversational turn always resolves through `/question/`.
